import { Injectable, NgZone } from '@angular/core';
import { Router, Event, NavigationEnd } from '@angular/router';
import { AuthService } from './auth.service';
import { BehaviorSubject, filter } from 'rxjs';
import { LoaderService } from '../shared/services/loader.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private readonly TIMEOUT_DURATION = 10 * 60 * 1000; // 10 minutos en milisegundos
  private inactivityTimer: any = null;
  private events: string[] = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
  private isSessionExpiredModalVisible = false;
  
  // Observable para notificar cuando la sesión ha expirado
  public sessionExpired = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
    private loaderService: LoaderService
  ) {
    // Suscribirse a eventos de navegación completada
    this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      // La navegación cuenta como actividad del usuario
      if (this.authService.isLoggedIn() && !this.isSessionExpiredModalVisible) {
        this.resetTimer();
      }
    });
  }

  /**
   * Inicializa el servicio de inactividad
   */
  init(): void {
    this.resetTimer();
    this.startWatching();
  }

  /**
   * Inicia la observación de eventos de actividad del usuario
   */
  private startWatching(): void {
    this.events.forEach(event => {
      window.addEventListener(event, () => this.resetTimer());
    });
  }

  /**
   * Detiene la observación de eventos
   */
  stopWatching(): void {
    this.events.forEach(event => {
      window.removeEventListener(event, () => this.resetTimer());
    });
    this.clearTimer();
  }

  /**
   * Reinicia el temporizador de inactividad
   */
  resetTimer(): void {
    // Si la modal de sesión expirada está visible, no reiniciar el timer
    if (this.isSessionExpiredModalVisible) {
      return;
    }

    this.clearTimer();
    
    // Usamos NgZone para asegurar que el temporizador funcione fuera de la detección de cambios de Angular
    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.onInactivityTimeout();
        });
      }, this.TIMEOUT_DURATION);
    });
  }

  /**
   * Limpia el temporizador actual
   */
  private clearTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Manejador para cuando se alcanza el tiempo de inactividad
   */
  private onInactivityTimeout(): void {
    if (this.authService.isLoggedIn()) {
      // Asegurarse de que el loader esté oculto antes de mostrar el modal
      this.loaderService.hide();
      
      this.isSessionExpiredModalVisible = true;
      this.sessionExpired.next(true);
      
      // No cerramos la sesión aquí, solo mostramos el modal
      // El cierre de sesión efectivo lo hace el usuario al hacer clic en el botón de reiniciar sesión
    }
  }

  /**
   * Reinicia el estado después de que el usuario haya interactuado con el modal de sesión expirada
   */
  resetSessionState(): void {
    this.isSessionExpiredModalVisible = false;
    this.sessionExpired.next(false);
    
    // Solo reiniciamos el timer si el usuario sigue logueado
    if (this.authService.isLoggedIn()) {
      this.resetTimer();
    }
  }
}